import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { LocationIcon } from '../Icons'
import { locationOptions } from '@/constants/event'
import GooglePlacesAutocomplete, {
  type PlaceData,
} from '../GooglePlacesAutocomplete'
import { useFormContext, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'

export default function EventLocationForm() {
  const { control, watch, setValue, clearErrors, trigger } =
    useFormContext<EventFormDataType>()
  const location = watch('location')

  const handlePlaceSelect = async (placeDetails: PlaceData) => {
    clearErrors([
      'venueAddress',
      'venueCity',
      'venueState',
      'venueCountry',
      'venueLatitude',
      'venueLongitude',
      'venueZipCode',
    ])
    setValue('venueAddress', placeDetails.address)
    setValue('venueCity', placeDetails.city)
    setValue('venueState', placeDetails.state)
    setValue('venueCountry', placeDetails.country)
    setValue('venueLatitude', placeDetails.lat)
    setValue('venueLongitude', placeDetails.lng)
    setValue('venueZipCode', placeDetails.zipCode)
    await trigger([
      'venueAddress',
      'venueCity',
      'venueState',
      'venueCountry',
      'venueLatitude',
      'venueLongitude',
      'venueZipCode',
    ])
  }

  const handleManualAddressChange = (value: string) => {
    if (value === '') {
      clearErrors([
        'venueAddress',
        'venueCity',
        'venueState',
        'venueCountry',
        'venueLatitude',
        'venueLongitude',
        'venueZipCode',
      ])
      setValue('venueCity', '')
      setValue('venueCountry', '')
      setValue('venueState', '')
      setValue('venueLatitude', undefined)
      setValue('venueLongitude', undefined)
      setValue('venueZipCode', '')
    } else {
      clearErrors(['venueAddress'])
    }
    setValue('venueAddress', value)
  }
  return (
    <>
      {/* Location */}
      <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <LocationIcon className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950">
          Location
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="space-y-2.5 max-w-[541px]">
          <Label className="text-sm font-semibold text-ss-neutral-950">
            Event Format
          </Label>
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="space-y-3 mb-4 flex gap-4"
                aria-label="Location method options"
              >
                {locationOptions.map(option => (
                  <div key={option.id} className="relative mb-0 gap-3">
                    <Label
                      htmlFor={option.id}
                      className={cn(
                        'flex items-center gap-1 rounded-lg cursor-pointer transition-all duration-200 ',
                        field.value === option.id
                          ? 'border-ss-blue-500 bg-white'
                          : 'border-border bg-background'
                      )}
                    >
                      <RadioGroupItem
                        value={option.id}
                        id={option.id}
                        className={cn(
                          field.value === option.id
                            ? 'border-ss-blue-500 bg-ss-blue-500 text-white'
                            : 'border-border bg-background'
                        )}
                        aria-label={option.title}
                      />
                      <div className="flex items-start gap-1 flex-1">
                        <div className="flex-1">
                          <div className="font-normal text-ss-neutral-950 text-sm leading-tight">
                            {option.title}
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {location !== locationOptions[1].id && (
            <div className="space-y-2">
              <Label
                variant="required"
                htmlFor="zoomLink"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Meeting Link
              </Label>
              <Controller
                name="zoomLink"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Input
                      {...field}
                      required
                      id="zoomLink"
                      type="text"
                      placeholder="Enter a meeting link..."
                      className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                    />
                    {error && (
                      <p className="text-sm text-ss-critical-200 mt-1">
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          )}
          {location !== locationOptions[0].id && (
            <>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="venueName"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Venue Name
                </Label>
                <Controller
                  name="venueName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        required
                        id="venueName"
                        type="text"
                        placeholder="Enter Venue Name"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="address"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Address
                </Label>
                <Controller
                  name="venueAddress"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <GooglePlacesAutocomplete
                        placeholder="Enter address"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                        value={field.value || ''}
                        handlePlaceSelect={handlePlaceSelect}
                        handleChange={e =>
                          handleManualAddressChange(e.target.value)
                        }
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="city"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  City
                </Label>
                <Controller
                  name="venueCity"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value || ''}
                        required
                        id="city"
                        type="text"
                        disabled
                        placeholder="Enter city"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="space-y-2 ">
                <Label
                  variant="required"
                  htmlFor="state"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  State
                </Label>
                <Controller
                  name="venueState"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value || ''}
                        required
                        id="state"
                        type="text"
                        disabled
                        placeholder="Enter state"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="venueCountry"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Country
                </Label>
                <Controller
                  name="venueCountry"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value || ''}
                        required
                        id="venueCountry"
                        type="text"
                        disabled
                        placeholder="Enter Country"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="venueZipCode"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Zip Code
                </Label>
                <Controller
                  name="venueZipCode"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value?.toString() || ''}
                        required
                        id="venueZipCode"
                        disabled
                        placeholder="Enter Zip Code"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="venueLatitude"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Latitude
                </Label>
                <Controller
                  name="venueLatitude"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value?.toString() || ''}
                        required
                        id="venueLatitude"
                        type="number"
                        disabled
                        placeholder="Enter Latitude"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  variant="required"
                  htmlFor="venueLongitude"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  Longitude
                </Label>
                <Controller
                  name="venueLongitude"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value?.toString() || ''}
                        required
                        id="venueLongitude"
                        type="number"
                        disabled
                        placeholder="Enter Longitude"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
            </>
          )}
        </div>
      </CardContent>
    </>
  )
}
